import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import RequiredLabel from "@/components/common/forms/requiredLabel";
import { TrashIcon } from "@/components/common/icons";
import { GREY } from "@/config/mui/colorPalette";
import { MedspaServicesAndProductsQuery } from "@/graphql/queries/settings/medspaServicesAndProducts.graphql.types";
import useMedspaServicesAndProducts from "@/hooks/settings/useMedspaServicesAndProducts";

export const getItemName = (
  itemType: PackageItemType,
  id: string,
  services: MedspaServicesAndProductsQuery["medspaByPk"]["serviceMenuItems"],
  serviceProducts: MedspaServicesAndProductsQuery["medspaByPk"]["serviceProducts"],
  products: MedspaServicesAndProductsQuery["medspaByPk"]["products"]
) => {
  switch (itemType) {
    case PackageItemType.SERVICE:
      return services.find((service) => service.id === id)?.name;
    case PackageItemType.SERVICE_PRODUCT:
      return serviceProducts.find((service) => service.id === id)
        ?.serviceProduct.name;
    case PackageItemType.PRODUCT:
      return products.find((product) => product.id === id)?.name;
    case PackageItemType.DOLLAR:
      return "Dollar Credit";
    default:
      return itemType satisfies never;
  }
};

export enum PackageItemType {
  SERVICE = "service",
  PRODUCT = "product",
  SERVICE_PRODUCT = "service_product", // must use "_" instead of "-" here because of the way the itemKey is formatted
  DOLLAR = "dollar",
}
export type PackageItemKey = `${PackageItemType}-${string}`;

const categoryMap = new Map([
  [PackageItemType.SERVICE, "Service"],
  [PackageItemType.PRODUCT, "Retail Product"],
  [PackageItemType.SERVICE_PRODUCT, "Service Product"],
  [PackageItemType.DOLLAR, "Deposit"],
]);

export const getItemCategory = (type: PackageItemType) => {
  return categoryMap.get(type) || "Unknown Category";
};

export default function PackageItemRow({
  packageItem,
  onRemove,
  index,
}: {
  packageItem: {
    itemKey: PackageItemKey;
    quantity: number;
  };
  onRemove: () => void;
  index: number;
}) {
  const [type, id] = packageItem.itemKey.split("-") as [
    PackageItemType,
    string,
  ];

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { data } = useMedspaServicesAndProducts();

  const { serviceMenuItems, products, serviceProducts } = data.medspaByPk;

  return (
    <Box
      sx={{
        border: `1px solid ${GREY[20]}`,
        borderRadius: "12px",
        p: 2,
        mb: 1.5,
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitleDefault">
          {getItemName(type, id, serviceMenuItems, serviceProducts, products)}
        </Typography>
        <IconButton onClick={onRemove} sx={{ p: 0 }}>
          <TrashIcon color={GREY[50]} />
        </IconButton>
      </Stack>
      <Typography variant="paragraphSmall">{getItemCategory(type)}</Typography>
      <Controller
        name={`packageItems.${index}.quantity`}
        control={control}
        render={({ field }) => {
          return (
            <TextField
              {...field}
              size="small"
              label={
                <RequiredLabel
                  label={
                    type === PackageItemType.DOLLAR
                      ? "Dollar Amount"
                      : "Quantity"
                  }
                />
              }
              type="text"
              sx={{ width: "130px", mt: 2 }}
              error={!!errors.packageItems?.[index]?.quantity?.message}
              helperText={
                errors.packageItems?.[index]?.quantity?.message as string
              }
              slotProps={{
                input: {
                  startAdornment: type === PackageItemType.DOLLAR && (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          );
        }}
      />
    </Box>
  );
}
